import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Building2, Users, DollarSign, LogIn, Pencil, Trash2, Plus,
  CreditCard, Gift, Shield, Activity, Search, FileText,
  AlertTriangle, CheckCircle, Clock, XCircle, RefreshCw
} from 'lucide-react';

const STATUS_CONFIG = {
  active:          { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Active' },
  trial:           { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock, label: 'Trial' },
  pending_payment: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CreditCard, label: 'Pending' },
  past_due:        { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertTriangle, label: 'Past Due' },
  canceled:        { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Canceled' },
};
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: 'bg-slate-100 text-slate-600 border-slate-200', label: status };
  return <Badge className={`text-xs border ${cfg.color}`}>{cfg.label}</Badge>;
};
const MetricCard = ({ label, value, sub, icon: Icon, color, format: fmt }) => {
  const display = fmt === 'currency'
    ? '$' + Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
    : (value || 0).toLocaleString();
  return (
    <Card className="bg-white shadow-sm" style={{ borderTop: `3px solid ${color}` }}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
          <Icon size={15} style={{ color }} />
        </div>
        <p className="text-2xl font-bold tabular-nums text-slate-900 font-heading">{display}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
};

export default function SuperAdminPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('monthly_desc');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDialog, setOrgDialog] = useState(null);
  const [overrideDialog, setOverrideDialog] = useState(null);
  const [orgForm, setOrgForm] = useState({ name: '', subscription_status: 'trial', plan: 'starter' });
  const [adminForm, setAdminForm] = useState({ username: '', password: '', full_name: '' });
  const [overrideForm, setOverrideForm] = useState({ free_locations: 0, custom_location_price: '', note: '' });
  const [savingOverride, setSavingOverride] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const STATUSES = ['trial', 'active', 'past_due', 'canceled', 'pending_payment'];

  const fetchStats = async (quiet = false) => {
    if (!quiet) setLoading(true); else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/superadmin/stats`);
      setStats(res.data);
    } catch { toast.error('Failed to load stats'); }
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { fetchStats(); }, []);

  const handleLoginAs = async (org) => {
    try {
      const saToken = localStorage.getItem('arch_token');
      localStorage.setItem('arch_sa_token', saToken);
      const res = await axios.post(`${API}/auth/impersonate/${org.id}`, {}, { headers: { Authorization: `Bearer ${saToken}` } });
      localStorage.setItem('arch_token', res.data.access_token);
      localStorage.setItem('arch_impersonating', JSON.stringify({ org_name: res.data.org_name, org_id: org.id }));
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      window.location.href = '/';
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const handleCreateOrg = async () => {
    if (!orgForm.name.trim()) { toast.error('Name required'); return; }
    setSavingOrg(true);
    try {
      const orgRes = await axios.post(`${API}/orgs`, orgForm);
      if (adminForm.username && adminForm.password) {
        await axios.post(`${API}/users`, { username: adminForm.username, password: adminForm.password, full_name: adminForm.full_name || adminForm.username, role: 'admin', org_id: orgRes.data.id });
      }
      toast.success('Organization created!');
      setOrgDialog(null);
      fetchStats(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    setSavingOrg(false);
  };

  const handleEditOrg = async () => {
    setSavingOrg(true);
    try {
      await axios.put(`${API}/orgs/${selectedOrg.id}`, orgForm);
      toast.success('Updated!');
      setOrgDialog(null);
      fetchStats(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    setSavingOrg(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this organization and all its data?')) return;
    try { await axios.delete(`${API}/orgs/${id}`); toast.success('Deleted'); fetchStats(true); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const openOverride = (org) => {
    const ov = org.billing_override || {};
    setOverrideForm({ free_locations: ov.free_locations ?? 0, custom_location_price: ov.custom_location_price != null ? String(ov.custom_location_price) : '', note: ov.note || '' });
    setOverrideDialog(org);
  };

  const handleSaveOverride = async () => {
    setSavingOverride(true);
    try {
      await axios.put(`${API}/superadmin/orgs/${overrideDialog.id}/billing-override`, {
        free_locations: parseInt(overrideForm.free_locations) || 0,
        custom_location_price: overrideForm.custom_location_price === '' ? null : parseFloat(overrideForm.custom_location_price),
        note: overrideForm.note
      });
      toast.success('Billing override saved!');
      setOverrideDialog(null);
      fetchStats(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    setSavingOverride(false);
  };

  const clearOverride = async (orgId) => {
    if (!window.confirm('Clear billing override?')) return;
    try { await axios.delete(`${API}/superadmin/orgs/${orgId}/billing-override`); toast.success('Override cleared'); fetchStats(true); }
    catch { toast.error('Failed'); }
  };

  const orgs = (stats?.org_details || []).filter(o => {
    const matchSearch = !search || o.name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || o.subscription_status === statusFilter;
    return matchSearch && matchStatus;
  }).sort((a, b) => {
    if (sortBy === 'monthly_desc') return (b.estimated_monthly || 0) - (a.estimated_monthly || 0);
    if (sortBy === 'monthly_asc') return (a.estimated_monthly || 0) - (b.estimated_monthly || 0);
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'activity') return (b.last_eod_date || '').localeCompare(a.last_eod_date || '');
    return 0;
  });

  const s = stats?.summary || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Shield size={20} className="text-blue-600" /> Super Admin
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Platform overview · {s.total_orgs || 0} organizations</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fetchStats(true)} disabled={refreshing}>
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => { setOrgForm({ name: '', subscription_status: 'trial', plan: 'starter' }); setAdminForm({ username: '', password: '', full_name: '' }); setOrgDialog('create'); }}>
            <Plus size={13} /> New Organization
          </Button>
        </div>
      </div>

      {/* Revenue + Subscription KPIs */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          {[...Array(7)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl bg-slate-100" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          <MetricCard label="MRR" value={s.monthly_recurring_revenue} format="currency" icon={DollarSign} color="#1D4ED8" sub={`ARR: $${((s.annual_recurring_revenue||0)/1000).toFixed(1)}k`} />
          <MetricCard label="Active" value={s.active_orgs} icon={CheckCircle} color="#16A34A" sub="paying" />
          <MetricCard label="Trial" value={s.trial_orgs} icon={Clock} color="#D97706" />
          <MetricCard label="Pending" value={s.pending_orgs} icon={CreditCard} color="#2563EB" sub="unpaid" />
          <MetricCard label="Past Due" value={s.pastdue_orgs} icon={AlertTriangle} color="#EA580C" />
          <MetricCard label="Users" value={s.total_users} icon={Users} color="#7C3AED" />
          <MetricCard label="Records" value={(s.total_eod_records||0)+(s.total_sale_records||0)} icon={FileText} color="#0891B2" sub={`${s.total_eod_records||0} EOD · ${s.total_sale_records||0} sales`} />
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search organizations..." className="h-8 text-xs pl-8 w-56" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs w-36"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map(st => <SelectItem key={st} value={st}>{STATUS_CONFIG[st]?.label || st}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="monthly_desc">Highest Revenue</SelectItem>
                <SelectItem value="monthly_asc">Lowest Revenue</SelectItem>
                <SelectItem value="name">Name A–Z</SelectItem>
                <SelectItem value="activity">Recent Activity</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400 ml-auto">{orgs.length} shown</span>
          </div>
        </CardContent>
      </Card>

      {/* Orgs Table */}
      <Card className="bg-white shadow-sm">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
<TableRow className="border-slate-100 bg-slate-50">
                  {['Organization','Contact','Email','Phone','Registered','Status','MRR','Coupon','Locations','Users','Actions'].map(h => (
                    <TableHead key={h} className="text-xs font-semibold text-slate-600">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? ([...Array(5)].map((_, i) => (
                  <TableRow key={i}>{[...Array(11)].map((_, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
                ))) : orgs.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="py-12 text-center text-slate-400">No organizations found</TableCell></TableRow>
                ) : orgs.map(org => (
                  <TableRow key={org.id} className="border-slate-50 hover:bg-slate-50">
                    <TableCell>
                      <p className="text-sm font-semibold text-slate-800">{org.name}</p>
                      {org.billing_override && <span className="text-xs text-purple-600 flex items-center gap-1"><Gift size={10}/> Custom billing</span>}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium text-slate-800">{org.admin_full_name || '—'}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-xs text-slate-600">{org.admin_email || '—'}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-xs text-slate-600">{org.admin_phone || '—'}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-xs text-slate-600">
                        {org.created_at ? new Date(org.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </p>
                    </TableCell>
                    <TableCell><StatusBadge status={org.subscription_status} /></TableCell>
                    <TableCell>
                      <p className="text-sm font-bold text-slate-900">${(org.estimated_monthly||249).toLocaleString()}<span className="text-xs text-slate-400 font-normal">/mo</span></p>
                      <p className="text-xs text-slate-400 capitalize">{org.plan||'starter'}</p>
                    </TableCell>
                    <TableCell>
                      {org.coupon_used ? (
                        <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-200">
                          <Gift size={10} className="mr-1" /> {org.coupon_code || 'Yes'}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{org.location_count||0}</TableCell>
                    <TableCell className="text-sm text-slate-700">{org.user_count||0}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 flex-wrap">
                        <Button size="sm" variant="ghost" className="h-7 px-2 gap-1 text-xs hover:text-blue-600" onClick={() => handleLoginAs(org)}>
                          <LogIn size={12}/> Sign In As
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-purple-600" onClick={() => openOverride(org)} title="Billing override">
                          <CreditCard size={12}/>
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setSelectedOrg(org); setOrgForm({ name: org.name, subscription_status: org.subscription_status, plan: org.plan||'starter' }); setOrgDialog('edit'); }}>
                          <Pencil size={12}/>
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-red-500" onClick={() => handleDelete(org.id)}>
                          <Trash2 size={12}/>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      {(stats?.recent_activity||[]).length > 0 && (
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm font-heading flex items-center gap-2"><Activity size={14} className="text-blue-600"/> Recent Platform Activity</CardTitle></CardHeader>
          <CardContent className="p-0 pb-3">
            <div className="divide-y divide-slate-50">
              {(stats.recent_activity||[]).map((a, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-2.5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${a.type==='sale'?'bg-green-100':'bg-blue-100'}`}>
                    {a.type==='sale'?<DollarSign size={11} className="text-green-600"/>:<FileText size={11} className="text-blue-600"/>}
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-medium text-slate-700">{a.org}</span>
                    <span className="text-xs text-slate-400 mx-1">·</span>
                    <span className="text-xs text-slate-500">{a.type==='sale'?`Sale $${(a.amount||0).toLocaleString()}`:'EOD submitted'}</span>
                  </div>
                  <span className="text-xs text-slate-400">{a.date}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={!!orgDialog} onOpenChange={o => !o && setOrgDialog(null)}>
        <DialogContent className="bg-white max-w-lg" hideClose={false}>
          <DialogHeader><DialogTitle className="font-heading">{orgDialog==='create'?'Create Organization':'Edit Organization'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1"><Label>Organization Name *</Label>
              <Input value={orgForm.name} onChange={e => setOrgForm({...orgForm,name:e.target.value})} placeholder="Bright Smile Dental Group" className="bg-white"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Status</Label>
                <Select value={orgForm.subscription_status} onValueChange={v => setOrgForm({...orgForm,subscription_status:v})}>
                  <SelectTrigger className="bg-white"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-white">{STATUSES.map(st => <SelectItem key={st} value={st}>{STATUS_CONFIG[st]?.label||st}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>Plan</Label>
                <Select value={orgForm.plan} onValueChange={v => setOrgForm({...orgForm,plan:v})}>
                  <SelectTrigger className="bg-white"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-white">{['starter','professional','enterprise'].map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            {orgDialog==='create' && (
              <div className="pt-2 border-t border-slate-100 space-y-3">
                <p className="text-sm font-medium text-slate-700">Admin User (optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Email</Label><Input value={adminForm.username} onChange={e=>setAdminForm({...adminForm,username:e.target.value})} placeholder="admin@clinic.com" className="bg-white"/></div>
                  <div className="space-y-1"><Label>Password</Label><Input type="password" value={adminForm.password} onChange={e=>setAdminForm({...adminForm,password:e.target.value})} placeholder="Password" className="bg-white"/></div>
                </div>
                <div className="space-y-1"><Label>Full Name</Label><Input value={adminForm.full_name} onChange={e=>setAdminForm({...adminForm,full_name:e.target.value})} placeholder="Dr. Jane Smith" className="bg-white"/></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setOrgDialog(null)}>Cancel</Button>
            <Button onClick={orgDialog==='create'?handleCreateOrg:handleEditOrg} disabled={savingOrg}>{savingOrg?'Saving…':orgDialog==='create'?'Create':'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Override Dialog */}
      <Dialog open={!!overrideDialog} onOpenChange={o => !o && setOverrideDialog(null)}>
        <DialogContent className="bg-white max-w-md" hideClose={false}>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CreditCard size={16} className="text-purple-500"/> Billing Override — {overrideDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">Standard: <strong>$249/mo base + $29/mo per additional location</strong></div>
            <div className="space-y-1"><Label>Free Additional Locations</Label>
              <p className="text-xs text-slate-400">e.g. 2 = 3 total locations before charges</p>
              <Input type="number" min="0" max="50" value={overrideForm.free_locations} onChange={e=>setOverrideForm(p=>({...p,free_locations:e.target.value}))} className="bg-white"/></div>
            <div className="space-y-1"><Label>Custom Location Price ($/mo)</Label>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <Input type="number" min="0" value={overrideForm.custom_location_price} onChange={e=>setOverrideForm(p=>({...p,custom_location_price:e.target.value}))} className="bg-white pl-7" placeholder="29"/></div></div>
            <div className="space-y-1"><Label>Internal Note</Label>
              <Textarea value={overrideForm.note} onChange={e=>setOverrideForm(p=>({...p,note:e.target.value}))} placeholder="e.g. Promo deal" className="bg-white text-sm min-h-[60px]"/></div>
            <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-xs text-purple-700 space-y-1">
              {(() => {
                const free=parseInt(overrideForm.free_locations)||0;
                const price=overrideForm.custom_location_price!==''?parseFloat(overrideForm.custom_location_price):29;
                const addLocs=Math.max(0,(overrideDialog?.location_count||0)-(1+free));
                const total=249+(addLocs*price);
                return (
                  <>
                    <div className="flex justify-between font-semibold"><span>Estimated total</span><span>${total}/mo</span></div>
                    <p className="text-purple-500">{(overrideDialog?.location_count||0)} locations · {1+free} included</p>
                  </>
                );
              })()}
            </div>
          </div>
          <DialogFooter className="gap-2">
            {overrideDialog?.billing_override && (
              <Button variant="ghost" className="text-red-400 hover:text-red-600 mr-auto" onClick={()=>{clearOverride(overrideDialog.id);setOverrideDialog(null);}}>Clear Override</Button>
            )}
            <Button variant="outline" className="bg-white" onClick={()=>setOverrideDialog(null)}>Cancel</Button>
            <Button onClick={handleSaveOverride} disabled={savingOverride} className="bg-purple-600 hover:bg-purple-500 text-white">{savingOverride?'Saving…':'Save Override'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
